import React, { useEffect, useState, useRef } from 'react';
import { Outlet, useLocation, NavLink, useNavigate } from 'react-router-dom';
import { Home, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../App';
import { useTranslation } from '../i18n/LanguageContext';
import LanguageSelector from './LanguageSelector';
import { UnreadAnnouncementsWidget } from './Announcements/AnnouncementsManager';
import { NotificationIcon } from './Notifications/NotificationIcon';
import ShiftZonePresenceTracker from './ShiftZonePresenceTracker';
import { API_BASE, SUPER_ADMIN_EMAIL } from '../config';
import { io } from 'socket.io-client';
import { setupWebPushForCurrentUser } from '../utils/pushNotifications';

/**
 * Layout principal pour les pages authentifiées
 * Gère le header, la navigation et affiche le contenu via Outlet
 * Company name: hamesha backend /api/auth/me se (companies.name by user's company_id) — admin/manager/employee same source
 */
// Fallback when API not loaded: only Starter plan modules (no Performance, Ideas, Announcements)
const DEFAULT_PLAN_FEATURES = ['pointage', 'historique', 'documents', 'audit_photos', 'profil', 'reports', 'billing', 'maps', 'gps', 'employees', 'help_faq'];

const hasFeature = (enabledFeatures, feature) => {
  const list = (enabledFeatures && enabledFeatures.length > 0) ? enabledFeatures : DEFAULT_PLAN_FEATURES;
  return list.includes(feature);
};

const MOBILE_BREAKPOINT = 768;
const WARNING_PATHS = ['/home'];

const isIOSDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isStandalonePwa = () => {
  if (typeof window === 'undefined') return false;
  // eslint-disable-next-line no-undef
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

const DashboardLayout = ({ enabledFeatures = [], currentPlan = null, subscriptionExpired = false, billingLoaded = false, billingData = null, onRefetchBilling }) => {
  const { user, logout, refreshUser } = useAuth();
  const { t, language } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isBillingPage = location.pathname === '/facturation' || location.pathname === '/facturation/plans' || location.pathname.startsWith('/facturation/');
  const isSuperAdmin = (user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || user?.role === 'super_admin';
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches);
  const [layoutDrawerOpen, setLayoutDrawerOpen] = useState(false);
  const [chatUnreadTotal, setChatUnreadTotal] = useState(0);
  const [notificationSocket, setNotificationSocket] = useState(null);
  const [bannerBusy, setBannerBusy] = useState(false);
  const [warningClosed, setWarningClosed] = useState(false);
  const pushSetupAttemptedRef = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;
  const locationRef = useRef(location);
  locationRef.current = location;

  // Live Chat: real-time badge + desktop notification (WhatsApp-style)
  const liveChatEnabled = hasFeature(enabledFeatures, 'live_chat');
  useEffect(() => {
    if (!liveChatEnabled) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const fetchUnread = () => {
      fetch(`${API_BASE}/api/chat/unread-total`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          if (d.success && typeof d.total === 'number') {
            setChatUnreadTotal(d.total);
            window.dispatchEvent(new CustomEvent('chat-unread-refresh', { detail: { total: d.total } }));
          }
        })
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 12000);
    const onRefresh = () => fetchUnread();
    window.addEventListener('chat-unread-refresh', onRefresh);
    const s = io(API_BASE, { path: '/socket.io', auth: { token }, transports: ['websocket', 'polling'] });
    setNotificationSocket(s);
    s.on('connect', () => { fetchUnread(); });
    s.on('connect_error', (err) => {
      console.error('[socket] connect_error', {
        apiBase: API_BASE,
        path: '/socket.io',
        message: err?.message || String(err)
      });
    });
    s.on('notification:new', () => {
      window.dispatchEvent(new CustomEvent('notification-refresh'));
    });
    s.on('message:new', (msg) => {
      fetchUnread();
      window.dispatchEvent(new CustomEvent('chat-unread-refresh'));
      const currentUserId = userRef.current?.id ?? userRef.current?.userId;
      const isFromMe = msg && currentUserId && String(msg.senderId) === String(currentUserId);
      if (isFromMe) return;
      const pathname = locationRef.current?.pathname ?? '';
      const showNotification = typeof document !== 'undefined' && (document.hidden || pathname !== '/live-chat');
      if (!showNotification || !msg) return;
      const requestAndShow = () => {
        if (!('Notification' in window)) return;
        if (Notification.permission === 'granted') {
          const title = t('live_chat.new_message') || 'Live Chat';
          const text = msg.attachmentName ? `📎 ${msg.attachmentName}` : (msg.content || '');
          const body = msg.senderName ? `${msg.senderName}: ${text.slice(0, 80)}${text.length > 80 ? '…' : ''}` : text.slice(0, 80);
          try {
            const n = new Notification(title, { body, icon: '/favicon.ico' });
            n.onclick = () => { n.close(); window.focus(); if (locationRef.current?.pathname !== '/live-chat') navigate('/live-chat'); };
          } catch (e) {}
        } else if (Notification.permission === 'default') {
          Notification.requestPermission().then((p) => { if (p === 'granted') requestAndShow(); });
        }
      };
      requestAndShow();
    });
    return () => {
      clearInterval(interval);
      window.removeEventListener('chat-unread-refresh', onRefresh);
      setNotificationSocket(null);
      s.disconnect();
    };
  }, [liveChatEnabled, t, navigate]);

  // Notification Center: real-time socket when live_chat is off (when on, chat effect sets notificationSocket)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || liveChatEnabled) return;
    const s = io(API_BASE, { path: '/socket.io', auth: { token }, transports: ['websocket', 'polling'] });
    setNotificationSocket(s);
    s.on('connect_error', (err) => {
      console.error('[socket] connect_error', {
        apiBase: API_BASE,
        path: '/socket.io',
        message: err?.message || String(err)
      });
    });
    s.on('notification:new', () => {
      window.dispatchEvent(new CustomEvent('notification-refresh'));
    });
    return () => { setNotificationSocket(null); s.disconnect(); };
  }, [liveChatEnabled]);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const update = () => {
      setIsMobile(mql.matches);
      if (!mql.matches) setLayoutDrawerOpen(false);
    };
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Keep push token setup reliable: run once after login/profile load.
  useEffect(() => {
    if (pushSetupAttemptedRef.current) return;
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[push] Skipped setup: missing auth token');
      return;
    }
    pushSetupAttemptedRef.current = true;
    console.log('[push] Calling setupWebPushForCurrentUser after login/profile load');
    setupWebPushForCurrentUser(token)
      .then((result) => {
        console.log('[push] setupWebPushForCurrentUser result', result);
      })
      .catch((e) => {
        console.error('[push] setupWebPushForCurrentUser failed', e);
      });
  }, [user?.id, user?.userId]);

  // When user visits a module directly, mark its notifications as read (so they disappear from bell)
  // Covers: leaves, training, expense report, announcements, idea box, documents, planning, performance, live chat, payroll, billing, badges
  const NOTIFICATION_MODULE_PATHS = [
    '/notes-frais',   // expense report
    '/conges',        // leaves
    '/communiques',   // announcements
    '/formations',    // training
    '/badges',
    '/performance',
    '/planning',
    '/ideas',         // idea box
    '/paie',          // payroll
    '/facturation',   // billing
    '/documents',
    '/live-chat'
  ];
  useEffect(() => {
    const basePath = '/' + (location.pathname.split('/').filter(Boolean)[0] || '');
    if (!NOTIFICATION_MODULE_PATHS.includes(basePath)) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/api/notifications/mark-read-by-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ link: basePath })
    })
      .then((r) => { if (r.ok) window.dispatchEvent(new CustomEvent('notification-refresh')); })
      .catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    if (refreshUser && user && (location.pathname === '/home' || location.pathname === '/dashboard' || location.pathname.startsWith('/admin/dashboard'))) {
      refreshUser();
    }
  }, [location.pathname, refreshUser, user]);

  const enableNotificationsFromBanner = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[push] Skipped setup: missing auth token');
      return;
    }
    setBannerBusy(true);
    try {
      const result = await setupWebPushForCurrentUser(token);
      console.log('[push] setupWebPushForCurrentUser result', result);
    } catch (e) {
      console.error('[push] setupWebPushForCurrentUser failed', e);
    } finally {
      setBannerBusy(false);
    }
  };

  const dismissWarningBanner = () => {
    setWarningClosed(true);
  };

  if (subscriptionExpired && !isBillingPage && !isSuperAdmin) {
    const status = (billingData?.status || '').toString().toLowerCase();
    const isCancelled = status === 'cancelled' || status === 'canceled';
    const isSuspended = status === 'suspended' || status === 'past_due' || status === 'unpaid';
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
        color: '#fff',
        padding: 24,
        textAlign: 'center'
      }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>⚠️</div>
        <h1 style={{ fontSize: 'clamp(22px, 4vw, 28px)', marginBottom: 12 }}>
          {isCancelled
            ? (t('billing.subscription_cancelled') || 'Subscription cancelled')
            : isSuspended
              ? (t('billing.account_suspended') || 'Account suspended')
              : (t('billing.subscription_expired') || 'Free trial ended')}
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: 24, maxWidth: 400 }}>
          {isCancelled
            ? (t('billing.subscription_cancelled_msg') || 'Your subscription has been cancelled and you can no longer access the dashboard. Choose a plan to reactivate your account.')
            : isSuspended
              ? (t('billing.account_suspended_msg') || 'Your account has been suspended. Please contact support.')
              : (t('billing.subscription_expired_msg') || 'Your 14-day free trial has ended. Choose a plan to continue using WorkBiFlow.')}
        </p>
        {!isSuspended && (
          <button
            type="button"
            onClick={() => {
              onRefetchBilling?.();
              navigate('/facturation/plans', { replace: true });
            }}
            style={{
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: '#fff',
              border: 'none',
              padding: '14px 28px',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)'
            }}
          >
            {t('billing.renew_plan') || 'Renouveler / Choisir un plan'}
          </button>
        )}
      </div>
    );
  }

  // Pages qui masquent le header et la navigation
  const pagesWithoutHeader = ['/home', '/formations', '/dashboard', '/conges', '/historique', '/badges', '/employees', '/super-admin', '/live-chat', '/pointage'];
  const shouldHideHeader = pagesWithoutHeader.some(path => location.pathname === path || location.pathname.startsWith(path));
  // Notifications page: keep top bar, hide middle menu bar only
  const showNavTabs = !shouldHideHeader && location.pathname !== '/notifications';

  const isAdminOrManager = ['admin', 'manager'].includes(user?.role);

  const isLiveChatPage = location.pathname === '/live-chat';
  const showNotificationBanner =
    isIOSDevice() &&
    WARNING_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`)) &&
    !warningClosed;

  return (
    <div style={{ 
      minHeight: '100vh',
      height: isLiveChatPage ? '100vh' : '100%',
      maxHeight: isLiveChatPage ? '100vh' : undefined,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      position: 'relative',
      overflow: isLiveChatPage ? 'hidden' : undefined
    }}>
      {!isSuperAdmin && <ShiftZonePresenceTracker />}
      {showNotificationBanner && (
        <div
          style={{
            position: 'fixed',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 1200,
            width: isMobile ? 'calc(100% - 16px)' : 'calc(100% - 24px)',
            background: '#fef3c7',
            border: '1px solid #f3df94',
            borderRadius: 8,
            padding: isMobile ? '10px 12px' : '11px 14px',
            color: '#5b4d1f',
            zIndex: 2500,
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
              <AlertTriangle size={16} color="#a16207" style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, lineHeight: 1.35 }}>
                  <strong>Notifications are not enabled.</strong> Enable browser notifications to receive notification on mobile.
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.45, marginTop: 4 }}>
                  On iPhone/iPad, open the website in Safari, tap Share, then Add to Home Screen before enabling notifications.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={dismissWarningBanner}
              aria-label="Close warning"
              title="Close"
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                border: '1px solid #e8d99a',
                background: 'transparent',
                color: '#8a7a3f',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <X size={15} />
            </button>
          </div>
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              onClick={enableNotificationsFromBanner}
              disabled={bannerBusy}
              style={{
                padding: '7px 12px',
                borderRadius: 8,
                border: 'none',
                background: bannerBusy ? '#eadba5' : '#f0c86b',
                color: '#3f2f09',
                cursor: bannerBusy ? 'wait' : 'pointer',
                fontWeight: 700,
                fontSize: 12.5
              }}
            >
              {bannerBusy ? 'Enabling...' : 'Enable Notifications'}
            </button>
          </div>
        </div>
      )}
      {/* Widget annonces et header - Masqués sur certaines pages; sur mobile avec drawer */}
      {!shouldHideHeader && (
        <>
          {!isSuperAdmin && <UnreadAnnouncementsWidget />}
          {isMobile ? (
            <>
              <div className="main-header layout-mobile-topbar" style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', maxWidth: 1200, margin: '0 auto' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    🎯 {user?.company || user?.companyName || '—'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => navigate('/home')}
                      aria-label={t('navigation.home') || 'Home'}
                      title={t('navigation.home') || 'Home'}
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'rgba(255,255,255,0.08)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        flexShrink: 0
                      }}
                    >
                      <Home size={22} />
                    </button>
                    <NotificationIcon socket={notificationSocket} />
                    <button
                    type="button"
                    onClick={() => setLayoutDrawerOpen(true)}
                    aria-label={t('home.open_menu') || 'Menu'}
                    title={t('home.open_menu') || 'Menu'}
                    style={{
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                  </button>
                </div>
              </div>
            </div>
              {layoutDrawerOpen && (
                <div
                  role="presentation"
                  onClick={() => setLayoutDrawerOpen(false)}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'flex-end' }}
                >
                  <div
                    role="dialog"
                    aria-modal="true"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: 'min(320px, 88vw)',
                      height: '100%',
                      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
                      padding: '20px 16px',
                      boxSizing: 'border-box',
                      overflowY: 'auto',
                      borderLeft: '1px solid rgba(255,255,255,0.12)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                      <span style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>{t('common.menu') || 'Menu'}</span>
                      <button type="button" onClick={() => setLayoutDrawerOpen(false)} aria-label={t('common.close') || 'Close'} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                    <h1 className="company-title" style={{ marginBottom: 4 }}>🎯 {user?.company || user?.companyName || '—'}</h1>
                    <p className="user-info" style={{ marginBottom: 16 }}>{t('common.welcome')}, {user.firstName} {user.lastName} ({user.role})</p>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <LanguageSelector style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 10px', borderRadius: 10 }} />
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="main-header">
              <div className="header-content">
                <div>
                  <h1 className="company-title">
                    🎯 {user?.company || user?.companyName || '—'}
                  </h1>
                  <p className="user-info">
                    {t('common.welcome')}, {user.firstName} {user.lastName} ({user.role})
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => navigate('/home')}
                    aria-label={t('navigation.home') || 'Home'}
                    title={t('navigation.home') || 'Home'}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.08)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  >
                    <Home size={22} />
                  </button>
                  <NotificationIcon socket={notificationSocket} />
                  <LanguageSelector />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Barre de navigation (menu) - Masquée sur certaines pages et sur /notifications */}
      {showNavTabs && (
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '20px'
        }}>
          <div className="nav-tabs">
            <NavLink
              to="/home"
              className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
            >
              🏠 {t('navigation.home')}
            </NavLink>
            
            {hasFeature(enabledFeatures, 'pointage') && (
            <NavLink
              to="/pointage"
              className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
            >
              📍 {t('navigation.attendance_gps') || 'Pointage GPS + Facial'}
            </NavLink>
            )}
            
            {user.role === 'admin' && (
              <>
                {hasFeature(enabledFeatures, 'pointage') && (
                <NavLink
                  to="/admin/gps"
                  className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
                  style={{
                    padding: '12px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    textDecoration: 'none'
                  }}
                >
                  🌍 {t('navigation.gps_config') || 'Configuration GPS'}
                </NavLink>
                )}
                {hasFeature(enabledFeatures, 'audit_photos') && (
                <NavLink
                  to="/admin/audit-photos"
                  className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
                  style={{
                    padding: '12px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    textDecoration: 'none'
                  }}
                >
                  📸 {t('navigation.audit_photos') || 'Audit Photos'}
                </NavLink>
                )}
              </>
            )}
            
            {hasFeature(enabledFeatures, 'historique') && (
            <NavLink
              to="/historique"
              className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
            >
              📋 {t('navigation.history')}
            </NavLink>
            )}
            
            {hasFeature(enabledFeatures, 'calendrier') && (
            <NavLink
              to="/calendrier"
              className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
            >
              📅 {t('navigation.calendar')}
            </NavLink>
            )}
            
            {hasFeature(enabledFeatures, 'planning') && (
            <NavLink
              to="/planning"
              className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
            >
              📊 {t('navigation.planning')}
            </NavLink>
            )}

            {hasFeature(enabledFeatures, 'ideas') && (
            <NavLink
              to="/ideas"
              className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
            >
              💡 {t('navigation.ideas')}
            </NavLink>
            )}

            {hasFeature(enabledFeatures, 'communiques') && (
            <NavLink
              to="/communiques"
              className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
            >
              📢 {t('navigation.announcements')}
            </NavLink>
            )}

            {isAdminOrManager && hasFeature(enabledFeatures, 'dashboard') && (
              <NavLink
                to="/dashboard"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
              >
                📊 {t('navigation.reports')}
              </NavLink>
            )}
            
            {isAdminOrManager && hasFeature(enabledFeatures, 'employees') && (
              <NavLink
                to="/employees"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
              >
                👥 {t('navigation.employees')}
              </NavLink>
            )}

            {isAdminOrManager && hasFeature(enabledFeatures, 'reports') && (
              <NavLink
                to="/rapports"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
              >
                📊 {t('navigation.reports')}
              </NavLink>
            )}

            {user && ['admin', 'manager'].includes(user.role) && hasFeature(enabledFeatures, 'badges') && (
              <NavLink
                to="/badges"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
              >
                🔖 {t('navigation.badges')}
              </NavLink>
            )}

            {user && ['admin', 'manager', 'employee'].includes(user.role) && hasFeature(enabledFeatures, 'formations') && (
              <NavLink
                to="/formations"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
              >
                🎓 {t('navigation.formations')}
              </NavLink>
            )}

            {user && ['admin', 'manager', 'employee'].includes(user.role) && hasFeature(enabledFeatures, 'notes_frais') && (
              <NavLink
                to="/notes-frais"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
              >
                💰 {t('navigation.expenses')}
              </NavLink>
            )}

            {hasFeature(enabledFeatures, 'live_chat') && (
            <NavLink
              to="/live-chat"
              className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
              style={{ position: 'relative' }}
            >
              💬 {t('home.live_chat') || t('navigation.live_chat') || 'Live Chat'}
              {chatUnreadTotal > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 8,
                    minWidth: 18,
                    height: 18,
                    borderRadius: 9,
                    background: '#ef4444',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px'
                  }}
                >
                  {chatUnreadTotal > 99 ? '99+' : chatUnreadTotal}
                </span>
              )}
            </NavLink>
            )}
            
            {hasFeature(enabledFeatures, 'conges') && (
            <NavLink
              to="/conges"
              className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
            >
              🏖️ {t('navigation.leaves')}
            </NavLink>
            )}

            {user.role === 'admin' && hasFeature(enabledFeatures, 'soldes') && (
              <NavLink
                to="/soldes"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
              >
                💰 {t('leaves.balance')} {t('settings.title')}
              </NavLink>
            )}
            
            {user.role === 'admin' && (
              <NavLink
                to="/facturation"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
              >
                💳 {t('navigation.billing')}
              </NavLink>
            )}

            {user.role === 'admin' && (
              <NavLink
                to="/facturation/plans"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
              >
                📋 {t('billing.choose_plan')}
              </NavLink>
            )}

            {((user.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || user.role === 'super_admin') && (
              <NavLink
                to="/super-admin"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
                style={({ isActive }) => ({
                  background: isActive ? '#dc3545' : '#f8f9fa',
                  color: isActive ? 'white' : '#333',
                  fontWeight: 'bold',
                  textDecoration: 'none'
                })}
              >
                🛡️ {t('admin.super_admin')}
              </NavLink>
            )}
          
            
            <div style={{ flex: 1 }} />
            <button
              onClick={logout}
              className="nav-tab logout-tab"
              style={{
                padding: '10px 14px',
                borderRadius: '10px',
                // background: 'linear-gradient(135deg,#ef4444,#dc2626)',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600,
                alignSelf: 'center'
              }}
            >
               {t('auth.logout')}
            </button>
          </div>

        </div>
      )}

      {/* Floating LanguageSelector - when header hidden so user can always change language; excluded on mobile for pages that have their own drawer; /employees has language in its own top bar (large screen) or drawer (mobile) */}
      {shouldHideHeader && location.pathname !== '/home' && location.pathname !== '/dashboard' && !location.pathname.startsWith('/employees') && !(location.pathname === '/historique' && isMobile) && !(location.pathname === '/conges' && isMobile) && !(location.pathname === '/formations' && isMobile) && !(location.pathname === '/badges' && isMobile) && !(location.pathname === '/pointage' && isMobile) && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 1200,
            display: 'flex',
            gap: 8,
            alignItems: 'center'
          }}
        >
          <LanguageSelector
            style={{
              background: 'rgba(10, 14, 39, 0.9)',
              padding: '8px 12px',
              borderRadius: 12,
              backdropFilter: 'blur(12px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          />
        </div>
      )}

      {/* If header is hidden for certain pages, show a small fixed logout button */}
      {/* {shouldHideHeader && (
        <button
          onClick={logout}
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 1200,
            padding: '8px 12px',
            borderRadius: 8,
            background: 'linear-gradient(135deg,#ef4444,#dc2626)',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 6px 18px rgba(0,0,0,0.25)'
          }}
        >
          🚪 {t('auth.logout')}
        </button>
      )} */}

      {/* Contenu des routes enfants - key={language} forces re-render when language changes */}
      {/* Live Chat: fix height to 100vh so only chat/list scroll internally, not the whole page */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
        overflow: isLiveChatPage ? 'hidden' : 'visible',
        ...(isLiveChatPage ? { height: '100vh', maxHeight: '100vh' } : {})
      }}>
        <Outlet key={language} context={{ enabledFeatures, currentPlan, billingLoaded, billingData, onRefetchBilling, notificationSocket }} />
      </div>
    </div>
  );
};

export default DashboardLayout;

